import { store } from "@/store";
import { severitiesFacadesApi } from "@/store/services/facades/endpoints/severities";
import type { FacadesBatchPostResponse } from "@/store/services/facades/types";
import { EncryptedSeveritiesAsset, supabaseApi } from "@/store/services/supabase";
import { dispatchWithRetries } from "@/store/services/supabase/utils";
import { severitiesActions } from "@/store/slices/severities";
import { is5xxHttpStatus } from "@/utils/http";
import * as Sentry from "@sentry/nextjs";
import type { SeveritiesJobSchema } from "../types";
import { decryptSeveritiesAsset, toSeveritiesBatchItem } from "./asset";

/** Rows posted to the Severities API per request. */
const BATCH_SIZE = 100;

/** Message for a row the batch rejects with a 422. `notifyPostError` checks for it to spot a format error. */
export const UNEXPECTED_FORMAT_ERROR = "The uploaded file does not match the expected format.";

/**
 * Posts a run's unprocessed rows to the Severities API in batches until none are left.
 * Starts from whichever rows are still unprocessed, so it also resumes a run left by a closed tab.
 * Throws the API's error messages if it rejects any row.
 */
export const postSeveritiesRun = async ({ id, secret, stamps, assets: { unprocessedCount } }: SeveritiesJobSchema) => {
  const runStamps = new Set(stamps);
  let unprocessed = unprocessedCount;

  const getUnprocessedAssets = () =>
    dispatchWithRetries<EncryptedSeveritiesAsset[]>({
      action: supabaseApi.endpoints.getUnprocessedSeveritiesAssets.initiate(
        { severityId: id, length: BATCH_SIZE },
        { forceRefetch: true, subscribe: false }
      ),
      maxRetries: 5,
      timeout: 5000,
    });

  let assets = await getUnprocessedAssets();
  while (assets.length) {
    const batch = await Promise.all(
      assets.map(async (asset) => {
        const { itemId, coordinates, data } = await decryptSeveritiesAsset(secret, asset);
        return JSON.stringify(toSeveritiesBatchItem({ itemId, coordinates, settings: data }));
      })
    );

    const response = await dispatchWithRetries<FacadesBatchPostResponse>({
      action: severitiesFacadesApi.endpoints.postSeveritiesBatch.initiate({ batch }),
      maxRetries: 5,
      shouldRetryFn: ({ error_summary }) =>
        error_summary?.some(({ status }) => is5xxHttpStatus(status))
          ? { retry: true, message: "Server has had an error. Will retry." }
          : { retry: false },
      timeout: 5000,
    });

    if (response.errors > 0) {
      Sentry.captureException("Severities Error: postSeveritiesRun", {
        tags: { section: "Severities Error" },
        extra: { severityId: id, errorSummary: response.error_summary },
      });

      throw (
        response.error_summary?.map(({ title, detail, status }) =>
          status === 422 ? UNEXPECTED_FORMAT_ERROR : `${title}: ${detail}`
        ) ?? ["Errors occured when uploading data - please try again"]
      );
    }

    await dispatchWithRetries<null>({
      action: supabaseApi.endpoints.setSeveritiesAssetsAsProcessed.initiate(assets.map((asset) => asset.id)),
      maxRetries: 5,
      timeout: 5000,
    });

    // Clamped, as the starting count misses rows inserted while the run was still being created.
    unprocessed = Math.max(0, unprocessed - assets.length);
    store.dispatch(severitiesActions.setRunUnprocessed({ id, count: unprocessed }));

    const stampCount = runStamps.size;
    response.stamps?.forEach((stamp) => runStamps.add(stamp));
    if (runStamps.size > stampCount) {
      await dispatchWithRetries<null>({
        action: supabaseApi.endpoints.updateSeveritiesStamps.initiate({ id, stamps: [...runStamps].sort() }),
        maxRetries: 5,
        timeout: 5000,
      });
    }

    assets = await getUnprocessedAssets();
  }

  store.dispatch(supabaseApi.util.invalidateTags([{ type: "Severities", id: "List" }]));
};
